import React from 'react';

import { FormControl } from '@mui/material';

import CustomModal from 'components/CustomModal';
import { Button } from 'components/Button';

import { FolderPath } from 'containers/Documents/components/FolderTreePicker/styles';
import { PathValue } from 'containers/Documents/components/MoveToFolderModal/styles';
import { StyledTitleText as TitleText } from 'containers/Dashboard/components/SideBar/components/Modals/FolderFormModalStyles';

import { OrgUnit, isIntake, nameTakenMessage, nameTakenUnder, unitPath } from '../types';

import UnitNameField from './UnitNameField';
import UnitPickerField from './UnitPickerField';
import { DialogContent } from './styles';

/** Intake holds records nobody could place, so nothing is ever created inside it. */
const intakeIsNotAParent = (units: OrgUnit[]): Map<string, string> => {
  const intake = units.flatMap((top) => top.children).find(isIntake);
  return new Map(intake ? [[intake.uuid, 'Intake only holds records waiting to be placed']] : []);
};

export interface AddUnitModalProps {
  open: boolean;
  units: OrgUnit[];
  initialParentId: string;
  onCancel: () => void;
  onConfirm: (name: string, parentId: string) => Promise<void>;
}

const AddUnitModal: React.FunctionComponent<AddUnitModalProps> = ({
  open,
  units,
  initialParentId,
  onCancel,
  onConfirm,
}) => {
  const [name, setName] = React.useState('');
  const [parentId, setParentId] = React.useState<string>(initialParentId);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setName('');
    setParentId(initialParentId);
    setSubmitting(false);
    // Only on opening: this clears what has been typed, so a change elsewhere must not fire it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = name.trim();
  // Not while submitting: the tree reloads before this dialog closes, and the unit just
  // created would make its own name look taken.
  const error =
    !submitting && trimmed && nameTakenUnder(units, parentId, trimmed)
      ? nameTakenMessage(units, parentId, trimmed)
      : null;
  const cannotAdd = submitting || trimmed.length === 0 || error !== null;

  const handleConfirm = (): void => {
    if (cannotAdd) return;
    setSubmitting(true);
    onConfirm(trimmed, parentId);
  };

  return (
    <CustomModal
      openModal={open}
      onClose={onCancel}
      modalTitle="Add unit"
      modalWidth="sm"
      cancelButton={
        <Button onClick={onCancel} variant="outlined" size="large">
          Cancel
        </Button>
      }
      submitButton={
        <Button onClick={handleConfirm} disabled={cannotAdd} variant="contained" size="large">
          Add unit
        </Button>
      }
    >
      <DialogContent data-testid="add-unit-dialog">
        <UnitNameField value={name} error={error} onChange={setName} onSubmit={handleConfirm} />
        <FormControl fullWidth>
          <TitleText as="label" htmlFor="parent-unit">
            Parent unit
          </TitleText>
          <UnitPickerField
            id="parent-unit"
            units={units}
            disabledReasons={intakeIsNotAParent(units)}
            value={parentId}
            onChange={setParentId}
            placeholder="Select a parent unit"
            label="Parent unit"
          />
        </FormControl>
        <FolderPath>
          New unit path: <PathValue>{`${unitPath(units, parentId)} / ${trimmed || 'new unit'}`}</PathValue>
        </FolderPath>
      </DialogContent>
    </CustomModal>
  );
};

export default AddUnitModal;
